import { Form, FormikProvider, useFormik } from 'formik';
import { core_v1_Transform } from '@antimatterhq/asl-api-ts-v1';
import { toFormikValidationSchema, toFormikValidate } from 'zod-formik-adapter';
import Spacer from '@antimatterhq/ui/components/spacer';
import { Text } from '@antimatterhq/ui/components/typography';
import { Button } from '@antimatterhq/ui/components/button';
import { AxiosError } from 'axios';
import { useNavigate } from 'react-router-dom';
import { TransformStreamForm } from './forms/TransformStreamForm';
import { TransformProcessingScheduleForm } from './forms/TransformProcessingScheduleForm';
import { TransformMetadataForm } from './forms/TransformMetadataForm';
import { TransformFilterForm } from './forms/TransfomFilterForm';
import { TransformOutputTableForm } from './forms/TransformOutputTableForm';
import {
  TransformSchema,
  TransformSchemaValues,
} from '@/zod/schemas/transforms/TransformSchema';
import { getTransformInitialValues } from '@/lib/transform/transformHelpers';
import { TableOfContentsProvider } from '@/contexts/TableOfContentContext';
import { TwoColumnLayout } from '@/components/container';
import { TableOfContents } from '@/components/tableOfContents/TableOfContents';
import { TocWrapper } from '@/components/tableOfContents/ToCWrapper';
import { ResponseError } from '@/components/responseError/ResponseError';
import { useDebounceValidation } from '@/hooks/formik/useDebounceValidation';

export type TransformFormProps = {
  transform: core_v1_Transform;
  responseError?: AxiosError<{ message: string }>;
  onSubmit: (values: TransformSchemaValues) => void;
  isSubmitting: boolean;
  isInEditMode: boolean;
};

export const TransformForm = ({
  transform,
  responseError,
  onSubmit,
  isSubmitting,
  isInEditMode,
}: TransformFormProps) => {
  const navigate = useNavigate();
  const formik = useFormik({
    initialValues: getTransformInitialValues(transform),
    validationSchema: toFormikValidationSchema(TransformSchema),
    validate: toFormikValidate(TransformSchema),
    validateOnChange: false,
    onSubmit,
  });
  useDebounceValidation(formik);

  return (
    <TableOfContentsProvider>
      <TwoColumnLayout.Root>
        <TwoColumnLayout.LeftColumn>
          <FormikProvider value={formik}>
            <Form>
              <Text size="2xl" bold className="mb-4">
                Create new transform
              </Text>

              <Spacer size="lg" />

              <TocWrapper name="transform-metadata" displayName="Metadata">
                <Text size="xl" bold>
                  Transform metadata
                </Text>
                <Spacer size="lg" />
                <TransformMetadataForm isInEditMode={isInEditMode} />
              </TocWrapper>

              <Spacer size="xl" />

              <TocWrapper
                name="transform-processing-schedule"
                displayName="Processing schedule"
              >
                <Text size="lg" bold>
                  Transform processing schedule
                </Text>
                <Spacer size="lg" />

                <TransformProcessingScheduleForm />
              </TocWrapper>

              <Spacer size="xl" />

              <TocWrapper
                name="transform-output-table"
                displayName="Destination table"
              >
                <Text size="xl" bold>
                  Destination table
                </Text>
                <Spacer size="lg" />

                <TransformOutputTableForm />
              </TocWrapper>

              <Spacer size="xl" />

              <TocWrapper name="stream" displayName="Source tables">
                <Text size="lg" bold>
                  Source tables
                </Text>
                <TransformStreamForm />
              </TocWrapper>

              <Spacer size="lg" />

              <TransformFilterForm />

              <Spacer size="2xl" />

              <ResponseError error={responseError} />

              <div className="flex justify-end gap-2">
                <Button
                  variant="default"
                  onClick={() => navigate('/configure/detection-rules')}
                  disabled={isSubmitting}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  type="submit"
                  disabled={isSubmitting}
                  loading={isSubmitting}
                >
                  {!isInEditMode && 'Create rule'}
                  {isInEditMode && 'Apply changes'}
                </Button>
              </div>
            </Form>
          </FormikProvider>
        </TwoColumnLayout.LeftColumn>
        <TwoColumnLayout.RightColumn>
          <TableOfContents className="sticky top-[48px]" />
        </TwoColumnLayout.RightColumn>
      </TwoColumnLayout.Root>
    </TableOfContentsProvider>
  );
};
